import React, { useState, useEffect } from "react";
import { Box, Typography, Card, Grid } from "@mui/material";
import ReactSpeedometer, { Transition } from "react-d3-speedometer";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

// Mock data generator for the charts
const generateMockData = () => {
  const data = [];
  const dates = [
    "Mar 18, 09:11", "Apr 8, 04:36", "May 5, 07:57", "May 12, 11:53",
    "Jun 9, 10:28", "Jun 18, 05:40", "Jun 23, 07:55", "Jul 25, 09:54",
    "Sep 29, 02:19", "Oct 1, 01:42", "Nov 20, 22:08", "Dec 7, 21:15",
    "Dec 9, 21:24", "Dec 21, 15:08", "Jan 6, 15:31", "Jan 21, 04:52",
    "Feb 15, 20:16", "Feb 17, 20:24", "Mar 3, 10:46", "Mar 5, 10:56",
    "Mar 7, 11:08", "Mar 9, 11:18", "Mar 19, 10:25", "Mar 21, 10:35",
    "Apr 3, 03:06", "Apr 5, 03:16", "Apr 9, 12:55", "Apr 25, 20:08",
  ];

  for (let i = 0; i < dates.length; i++) {
    data.push({
      time: dates[i],
      quality: 800 + Math.random() * 400, // Random values between 800-1200
      temperature: 30 + Math.random() * 40, // Random values between 30-70
    });
  }
  return data;
};

const OilPage = () => {
  const [chartData, setChartData] = useState<any[]>([]);

  useEffect(() => {
    setChartData(generateMockData());
  }, []);

  return (
    <Box sx={{ p: 3, backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
      {/* Top Header */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
        <Box
          sx={{
            backgroundColor: "#2c5282", // Dark blue matching the screenshot
            color: "white",
            padding: "8px 24px",
            borderRadius: "4px",
            fontWeight: "bold",
            fontSize: "1.1rem",
            textTransform: "uppercase",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          GEARBOX STBD ENGINE
        </Box>
      </Box>

      {/* Top Cards Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Oil Temperature Gauge */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              p: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              height: "100%",
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 4 }}>
              Oil Temperature (°C)
            </Typography>

            <Box sx={{ height: 200 }}>
              <ReactSpeedometer
                maxValue={100}
                minValue={-20}
                value={35.04}
                needleColor="#4a5568"
                segments={6}
                customSegmentStops={[-20, 0, 20, 40, 60, 80, 100]}
                segmentColors={[
                  "#3182ce", // Blue
                  "#63b3ed", // Light blue
                  "#e2e8f0", // Grey
                  "#fed7d7", // Light Red
                  "#f56565", // Red
                  "#c53030", // Dark Red
                ]}
                currentValueText="35.04"
                textColor="#333"
                width={300}
                ringWidth={25}
                needleTransitionDuration={2000}
                needleTransition={Transition.easeElastic}
                valueTextFontSize="22px"
              />
            </Box>

            <Typography variant="body2" sx={{ color: "#718096", mt: 2 }}>
              Updated On: 2025/04/29 04:34:06
            </Typography>
          </Card>
        </Grid>

        {/* Oil Quality Index Gauge */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              p: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              height: "100%",
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 4 }}>
              Oil Quality Index
            </Typography>

            <Box sx={{ height: 200 }}>
              <ReactSpeedometer
                maxValue={1400}
                minValue={200}
                value={1088}
                needleColor="#4a5568"
                segments={6}
                customSegmentStops={[200, 400, 600, 800, 1000, 1200, 1400]}
                segmentColors={[
                  "#e53e3e", // Red
                  "#dd6b20", // Orange
                  "#d69e2e", // Yellow
                  "#38a169", // Green
                  "#68d391", // Light green
                  "#a0aec0", // Grey
                ]}
                currentValueText="1088"
                textColor="#333"
                width={300}
                ringWidth={25}
                needleTransitionDuration={2000}
                needleTransition={Transition.easeElastic}
                valueTextFontSize="22px"
              />
            </Box>

            <Typography
              variant="subtitle1"
              sx={{ color: "#38a169", fontWeight: "bold", mt: 1 }}
            >
              Good Oil Condition
            </Typography>

            <Typography variant="body2" sx={{ color: "#718096" }}>
              Updated On : 2025/04/29 04:34:06
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Bottom Chart Card */}
      <Card sx={{ p: 3, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 3 }}>
          Trends Graph for Oil Quality Index and Temperature
        </Typography>

        <Grid container spacing={4}>
          {/* Quality Chart */}
          <Grid item xs={12} md={6}>
            <Box sx={{ height: 350, width: "100%" }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={chartData}
                  margin={{ top: 10, right: 10, left: -20, bottom: 60 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={true} />
                  <XAxis
                    dataKey="time"
                    angle={-45}
                    textAnchor="end"
                    tick={{ fontSize: 10 }}
                    height={70}
                  />
                  <YAxis
                    domain={[200, 1400]}
                    tickCount={7}
                    tick={{ fontSize: 11 }}
                    label={{
                      value: "Oil Quality Index",
                      angle: -90,
                      position: "insideLeft",
                      offset: 25,
                      style: { textAnchor: "middle", fill: "#666" },
                    }}
                  />
                  <Tooltip />
                  <Legend verticalAlign="top" height={36} />
                  <Line
                    type="stepAfter"
                    dataKey="quality"
                    name="Oil Quality Index"
                    stroke="#2c3e50"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Grid>

          {/* Temperature Chart */}
          <Grid item xs={12} md={6}>
            <Box sx={{ height: 350, width: "100%" }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={chartData}
                  margin={{ top: 10, right: 10, left: -20, bottom: 60 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={true} />
                  <XAxis
                    dataKey="time"
                    angle={-45}
                    textAnchor="end"
                    tick={{ fontSize: 10 }}
                    height={70}
                  />
                  <YAxis
                    domain={[0, 100]}
                    tickCount={11}
                    tick={{ fontSize: 11 }}
                    label={{
                      value: "Oil Temperature (°C)",
                      angle: -90,
                      position: "insideLeft",
                      offset: 25,
                      style: { textAnchor: "middle", fill: "#666" },
                    }}
                  />
                  <Tooltip />
                  <Legend verticalAlign="top" height={36} />
                  <Line
                    type="stepAfter"
                    dataKey="temperature"
                    name="Oil Temperature (°C)"
                    stroke="#2c3e50"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Grid>
        </Grid>
      </Card>
    </Box>
  );
};

export default OilPage;
